/**
 * Inspect a HITL response payload and return a one-line summary suitable
 * for the collapsed card header and the pipeline-badge tooltip. Recognises
 * the common shapes produced by current workflows:
 *   - { decision: "approve" | "reject" | "approve_with_corrections", note?: string }
 *   - { response: "accept" | "decline", note?: string }
 *   - { choice: string, ... }
 *   - presence of `corrections` → "Approved with corrections"
 *
 * Falls through to a best-effort dump when the shape is unrecognized so
 * callers always receive a non-empty string.
 */

export type HitlBadgeKind = "approve" | "reject" | "corrections" | "timeout" | "unknown";

const APPROVE_VALUES = new Set([
  "approve",
  "approved",
  "accept",
  "accepted",
  "yes",
  "confirm",
  "confirmed",
  "ok",
]);

const REJECT_VALUES = new Set([
  "reject",
  "rejected",
  "decline",
  "declined",
  "no",
  "deny",
  "denied",
  "cancel",
]);

const CORRECTIONS_VALUES = new Set([
  "approve_with_corrections",
  "approved_with_corrections",
  "corrections",
  "edit",
  "modify",
]);

function pickDecisionToken(response: Record<string, unknown> | null | undefined): string | null {
  if (!response) return null;
  for (const key of ["decision", "response", "choice", "action", "result"]) {
    const v = response[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim().toLowerCase();
  }
  return null;
}

function pickNote(response: Record<string, unknown> | null | undefined): string | null {
  if (!response) return null;
  for (const key of ["note", "reason", "comment", "comments", "feedback", "message"]) {
    const v = response[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return null;
}

export function classifyHitlResponse(
  response: Record<string, unknown> | null | undefined,
  eventType: string = "decision",
): HitlBadgeKind {
  if (eventType === "timed_out" || eventType === "cancelled") return "timeout";
  if (!response) return "unknown";
  if (response.corrections != null) return "corrections";
  const token = pickDecisionToken(response);
  if (!token) return "unknown";
  if (CORRECTIONS_VALUES.has(token)) return "corrections";
  if (APPROVE_VALUES.has(token)) return "approve";
  if (REJECT_VALUES.has(token)) return "reject";
  return "unknown";
}

export function summarizeHitlResponse(
  response: Record<string, unknown> | null | undefined,
  eventType: string = "decision",
): string {
  if (eventType === "timed_out") return "Timed out — no decision";
  if (eventType === "cancelled") return "Cancelled before decision";

  const kind = classifyHitlResponse(response, eventType);
  const note = pickNote(response);
  let label: string;
  switch (kind) {
    case "approve":
      label = "Approved";
      break;
    case "reject":
      label = "Rejected";
      break;
    case "corrections":
      label = "Approved with corrections";
      break;
    case "timeout":
      label = "Timed out";
      break;
    default: {
      // Fall through — show the first string-typed value we find as a best
      // effort so the card header is never blank.
      if (response) {
        for (const value of Object.values(response)) {
          if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
          }
        }
      }
      label = "Decision recorded";
    }
  }
  return note ? `${label} · ${note}` : label;
}
